import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';
import { getGoldenWindowRating, getGoldenWindowShortRating, getGoldenWindowRatingColor } from '../../../../utils/goldenWindowRating';
import { ForecastBinaryService } from '../../../../services/ForecastBinaryService';
import TierAccessService from '../../../../services/TierAccessService';

export class SolarAlpenglowWidget implements WidgetRenderer {
  private getScoreColor(score: number): string {
    return getGoldenWindowRatingColor(score);
  }

  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'Sunrise, Sunset and Golden Hour Outlooks';
    const data = ctx.data;
    const showSunrise = widget.options?.showSunrise !== false;
    const showSunset = widget.options?.showSunset !== false;
    const showAlpenglow = widget.options?.showAlpenglow !== false;
    const showGoldenHour = widget.options?.showGoldenHour !== false;
    const layoutStyle = widget.options?.layoutStyle || 'auto';

    const outlook = data.solarOutlook || [];
    if (!data.hasBinaryPointData && !TierAccessService.userHasLayerAccess('sunrise')) {
      return `
        <div class="metric-card" style="background: rgba(30, 41, 59, 0.75); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
            <h3 style="margin: 0; color: #f8fafc; font-size: 0.95rem; font-weight: 700;">${title}</h3>
            <span style="font-size: 0.7rem; color: #10b981; font-weight: 600; border: 1px solid #10b981; padding: 1px 4px; border-radius: 4px;">Proprietary Blend</span>
          </div>
          ${TierAccessService.buildTrendChartDenialHtml({
            denial: data?.accessDenial || ForecastBinaryService.lastAccessDenial,
            requiredTier: 'pro',
            featureLabel: 'sunrise, sunset, golden hour & alpenglow quality forecasts'
          })}
        </div>
      `;
    }
    if (!outlook.length) {
      return `
        <div class="metric-card" style="background: rgba(30, 41, 59, 0.75); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; color: #94a3b8; font-size: 0.8rem; text-align: center;">
          No solar & alpenglow outlook available
        </div>
      `;
    }

    const isDesktop = typeof window !== 'undefined' ? window.innerWidth >= 640 : true;
    const effectiveStyle = layoutStyle === 'auto' ? (isDesktop ? 'web' : 'mobile') : layoutStyle;

    let contentHtml = '';

    if (effectiveStyle === 'web') {
      // CONCEPT 3: Web-Optimized Matrix Table
      const rows: Array<{ label: string; getter: (s: any) => { score: number; text: string } }> = [];
      if (showSunrise) rows.push({ label: '🌅 Sunrise Quality', getter: s => ({ score: s.sunriseScore, text: s.sunriseTime || '' }) });
      if (showSunset) rows.push({ label: '🌇 Sunset Quality', getter: s => ({ score: s.sunsetScore, text: s.sunsetTime || '' }) });
      if (showGoldenHour) {
        rows.push({
          label: '✨ Golden Hr (Morning)',
          getter: s => {
            const sc = s.goldenHourMornScore !== undefined ? s.goldenHourMornScore : Math.round((s.sunriseScore + s.alpenglowMorn) / 2);
            return { score: sc, text: s.goldenHourMorn || '' };
          }
        });
        rows.push({
          label: '✨ Golden Hr (Evening)',
          getter: s => {
            const sc = s.goldenHourEveScore !== undefined ? s.goldenHourEveScore : Math.round((s.sunsetScore + s.alpenglowEve) / 2);
            return { score: sc, text: s.goldenHourEve || '' };
          }
        });
      }
      if (showAlpenglow) {
        rows.push({ label: '🏔️ Alpenglow (Morning)', getter: s => ({ score: s.alpenglowMorn, text: '' }) });
        rows.push({ label: '🏔️ Alpenglow (Evening)', getter: s => ({ score: s.alpenglowEve, text: '' }) });
      }

      const thCols = outlook.map((s: any) => `
        <th style="padding: 7px 10px; font-size: 0.72rem; color: #94a3b8; text-align: center; border-bottom: 1px solid rgba(255,255,255,0.08); font-weight: 700; text-transform: uppercase;">
          ${s.date}
        </th>
      `).join('');

      const trRows = rows.map(r => {
        const tdCols = outlook.map((s: any) => {
          const res = r.getter(s);
          const color = this.getScoreColor(res.score);
          const fullRating = getGoldenWindowRating(res.score);
          const shortRating = getGoldenWindowShortRating(res.score);
          return `
            <td style="padding: 6px 10px; text-align: center; border-bottom: 1px solid rgba(255,255,255,0.04);">
              <span title="${fullRating}" style="display: inline-flex; align-items: center; justify-content: center; gap: 4px; font-family: monospace; font-weight: 700; font-size: 0.8rem; color: ${color}; background: rgba(0,0,0,0.25); padding: 2px 7px; border-radius: 4px; border: 1px solid ${color}44;">
                ${Number(res.score).toFixed(0)} <span style="font-size: 0.65rem; text-transform: uppercase; opacity: 0.85;">${shortRating}</span>
              </span>
            </td>
          `;
        }).join('');
        return `
          <tr>
            <td style="padding: 6px 12px; font-size: 0.75rem; font-weight: 600; color: #fff; background: rgba(20, 27, 45, 0.4); border-bottom: 1px solid rgba(255,255,255,0.04); white-space: nowrap;">
              ${r.label}
            </td>
            ${tdCols}
          </tr>
        `;
      }).join('');

      contentHtml = `
        <div style="overflow-x: auto; border: 1px solid rgba(255,255,255,0.06); border-radius: 8px; background: rgba(15, 23, 42, 0.4);">
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background: rgba(20, 27, 45, 0.75);">
                <th style="padding: 7px 12px; font-size: 0.72rem; color: #94a3b8; text-align: left; border-bottom: 1px solid rgba(255,255,255,0.08); font-weight: 700; text-transform: uppercase;">
                  Metric / Phase
                </th>
                ${thCols}
              </tr>
            </thead>
            <tbody>
              ${trRows}
            </tbody>
          </table>
        </div>
      `;
    } else {
      // CONCEPT 2A: Mobile-Optimized Daily Optical List
      const rowsHtml = outlook.map((s: any) => {
        const ghMornScore = s.goldenHourMornScore !== undefined ? s.goldenHourMornScore : Math.round((s.sunriseScore + s.alpenglowMorn) / 2);
        const ghEveScore = s.goldenHourEveScore !== undefined ? s.goldenHourEveScore : Math.round((s.sunsetScore + s.alpenglowEve) / 2);

        const pills = [];
        if (showSunrise) {
          const color = this.getScoreColor(s.sunriseScore);
          pills.push(`
            <span style="display: inline-flex; align-items: center; gap: 4px; background: rgba(0,0,0,0.3); border: 1px solid ${color}44; color: ${color}; padding: 3px 7px; border-radius: 6px; font-size: 0.72rem;">
              <span>🌅 Sunrise</span>
              <strong style="font-family: monospace; font-size: 0.78rem;">${Number(s.sunriseScore).toFixed(0)}</strong>
            </span>
          `);
        }
        if (showSunset) {
          const color = this.getScoreColor(s.sunsetScore);
          pills.push(`
            <span style="display: inline-flex; align-items: center; gap: 4px; background: rgba(0,0,0,0.3); border: 1px solid ${color}44; color: ${color}; padding: 3px 7px; border-radius: 6px; font-size: 0.72rem;">
              <span>🌇 Sunset</span>
              <strong style="font-family: monospace; font-size: 0.78rem;">${Number(s.sunsetScore).toFixed(0)}</strong>
            </span>
          `);
        }
        if (showGoldenHour) {
          const colorM = this.getScoreColor(ghMornScore);
          const colorE = this.getScoreColor(ghEveScore);
          pills.push(`
            <span style="display: inline-flex; align-items: center; gap: 4px; background: rgba(0,0,0,0.3); border: 1px solid ${colorM}44; color: ${colorM}; padding: 3px 7px; border-radius: 6px; font-size: 0.72rem;">
              <span>✨ GH (M)</span>
              <strong style="font-family: monospace; font-size: 0.78rem;">${Number(ghMornScore).toFixed(0)}</strong>
            </span>
          `);
          pills.push(`
            <span style="display: inline-flex; align-items: center; gap: 4px; background: rgba(0,0,0,0.3); border: 1px solid ${colorE}44; color: ${colorE}; padding: 3px 7px; border-radius: 6px; font-size: 0.72rem;">
              <span>✨ GH (E)</span>
              <strong style="font-family: monospace; font-size: 0.78rem;">${Number(ghEveScore).toFixed(0)}</strong>
            </span>
          `);
        }
        if (showAlpenglow) {
          const colorM = this.getScoreColor(s.alpenglowMorn);
          const colorE = this.getScoreColor(s.alpenglowEve);
          pills.push(`
            <span style="display: inline-flex; align-items: center; gap: 4px; background: rgba(0,0,0,0.3); border: 1px solid ${colorM}44; color: ${colorM}; padding: 3px 7px; border-radius: 6px; font-size: 0.72rem;">
              <span>🏔️ Alpen (M)</span>
              <strong style="font-family: monospace; font-size: 0.78rem;">${Number(s.alpenglowMorn).toFixed(0)}</strong>
            </span>
          `);
          pills.push(`
            <span style="display: inline-flex; align-items: center; gap: 4px; background: rgba(0,0,0,0.3); border: 1px solid ${colorE}44; color: ${colorE}; padding: 3px 7px; border-radius: 6px; font-size: 0.72rem;">
              <span>🏔️ Alpen (E)</span>
              <strong style="font-family: monospace; font-size: 0.78rem;">${Number(s.alpenglowEve).toFixed(0)}</strong>
            </span>
          `);
        }

        return `
          <div style="display: flex; justify-content: space-between; align-items: center; gap: 8px; padding: 6px 10px; background: rgba(30, 41, 59, 0.4); border: 1px solid rgba(255,255,255,0.05); border-radius: 8px; margin-bottom: 4px;">
            <div style="min-width: 90px;">
              <div style="font-size: 0.78rem; font-weight: 700; color: #fff;">${s.date}</div>
              <div style="font-size: 0.68rem; color: #94a3b8; font-family: monospace;">${s.sunriseTime} / ${s.sunsetTime}</div>
            </div>
            <div style="display: flex; flex-wrap: wrap; gap: 5px; justify-content: flex-end;">
              ${pills.join('')}
            </div>
          </div>
        `;
      }).join('');

      contentHtml = `
        <div style="display: flex; flex-direction: column; gap: 2px;">
          ${rowsHtml}
        </div>
      `;
    }

    return `
      <div class="metric-card" style="background: rgba(30, 41, 59, 0.75); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
          <h3 style="margin: 0; color: #f8fafc; font-size: 0.95rem; font-weight: 700; display: flex; align-items: center; gap: 6px;">
            <span>${title}</span>
          </h3>
          <span style="font-size: 0.7rem; color: #10b981; font-weight: 600; border: 1px solid #10b981; padding: 1px 4px; border-radius: 4px;">Proprietary Blend</span>
        </div>
        ${contentHtml}
      </div>
    `;
  }

  public init(_widget: ForecastWidgetConfig, _ctx: WidgetRenderContext): void {
    // Pure DOM rendering, no asynchronous map or chart canvas
  }
}
